/**
 * LoginPage with 2-step MFA challenge.
 *
 * Step 1: email + password → login()
 *   - If OK: AuthProvider hydrates → redirects via router
 *   - If mfa_required: show challenge step (TOTP code or recovery)
 *     - If enrolment_required: user has been forced by policy; run enrol flow
 *       (QR, recovery codes, code) inline.
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Logo from '../branding/Logo';

export default function LoginPage() {
  const { login, mfaChallenge, mfaEnrol, mfaEnrolVerify } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // MFA state (step 2)
  const [mfaStep, setMfaStep] = useState(null);  // null | 'code' | 'enrol'
  const [challengeToken, setChallengeToken] = useState('');
  const [code, setCode] = useState('');
  const [enrolData, setEnrolData] = useState(null); // {secret, qr_data_url, recovery_codes}
  const [revealRecovery, setRevealRecovery] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const r = await login(email.trim().toLowerCase(), password);
      if (r.mfa_required) {
        setChallengeToken(r.challenge_token);
        if (r.mfa_enrolment_required) {
          // Kick off forced-enrolment flow
          try {
            const setup = await mfaEnrol(r.challenge_token);
            setEnrolData(setup);
            setMfaStep('enrol');
          } catch (enrErr) {
            setError(enrErr.message);
          }
        } else {
          setMfaStep('code');
        }
      }
    } catch (err) {
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('429') || msg.includes('too many')) {
        setError('Too many attempts. Please wait 15 minutes and try again.');
      } else if (msg.includes('invalid') || msg.includes('401')) {
        setError('Email or password is incorrect.');
      } else {
        setError(err?.message || 'Login failed. Please try again.');
      }
    } finally { setSubmitting(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      await mfaChallenge(challengeToken, code.trim());
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const submitEnrolCode = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      await mfaEnrolVerify(challengeToken, code.trim());
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="auth-shell" data-testid="login-page">
      <div className="auth-card" style={{ maxWidth: mfaStep === 'enrol' ? 600 : 420 }}>
        <div className="auth-brand">
          <Logo variant="auth" imgClassName="auth-logo" data-testid="login-brand-logo" />
        </div>

        {!mfaStep && (
          <>
            <h1 className="auth-title">Sign in to QUOD-Finder</h1>
            <p className="auth-subtitle">Invite-only platform — please use the credentials shared with you.</p>
            <form onSubmit={submit} className="auth-form" data-testid="login-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" autoComplete="email" required autoFocus
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="you@company.com" data-testid="login-email-input" />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input id="password" type="password" autoComplete="current-password" required
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       placeholder="Your password" data-testid="login-password-input" />
              </div>
              {error && <div className="auth-error" data-testid="login-error">{error}</div>}
              <button type="submit" className="btn btn-primary"
                      disabled={submitting || !email || !password}
                      data-testid="login-submit-button" style={{ width: '100%', marginTop: 4 }}>
                {submitting ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          </>
        )}

        {mfaStep === 'code' && (
          <>
            <h1 className="auth-title">Verify MFA code</h1>
            <p className="auth-subtitle">Enter the 6-digit code from your authenticator app.
              Or a recovery code if you've lost the device.</p>
            <form onSubmit={submitCode} className="auth-form" data-testid="mfa-challenge-form">
              <div className="form-group">
                <input type="text" autoFocus maxLength={25} value={code}
                       onChange={(e) => setCode(e.target.value)}
                       placeholder="123456 or recovery code"
                       data-testid="mfa-challenge-code-input"
                       style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, letterSpacing: 2 }} />
              </div>
              {error && <div className="auth-error" data-testid="mfa-challenge-error">{error}</div>}
              <button type="submit" className="btn btn-primary" disabled={submitting || !code}
                      data-testid="mfa-challenge-submit" style={{ width: '100%' }}>
                {submitting ? 'Verifying…' : 'Verify'}
              </button>
            </form>
          </>
        )}

        {mfaStep === 'enrol' && enrolData && (
          <>
            <h1 className="auth-title">Enable MFA to continue</h1>
            <p className="auth-subtitle">Platform policy requires MFA on this account.
              Set it up now — takes about 30 seconds.</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <h4 style={{ margin: '0 0 4px', fontSize: 11, textTransform: 'uppercase' }}>1. Scan</h4>
                <img src={enrolData.qr_data_url} alt="TOTP QR"
                     style={{ width: 180, height: 180, background: '#fff', padding: 8, border: '1px solid var(--border)', borderRadius: 4 }} />
                <div style={{ fontSize: 10, marginTop: 6, color: 'var(--text-secondary)' }}>
                  Manual key:
                  <div data-testid="mfa-enrol-secret" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--text)', wordBreak: 'break-all' }}>
                    {enrolData.secret}
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 11, textTransform: 'uppercase' }}>2. Save recovery codes</h4>
                <div style={{ background: 'var(--bg)', padding: 6, borderRadius: 4, fontSize: 10, marginBottom: 6 }}>
                  These won't be shown again. Keep them safe.
                </div>
                {!revealRecovery ? (
                  <button type="button" className="btn btn-outline btn-sm"
                          onClick={() => setRevealRecovery(true)}
                          data-testid="mfa-enrol-reveal-btn">Reveal codes</button>
                ) : (
                  <pre data-testid="mfa-enrol-recovery-codes"
                       style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, background: 'var(--bg)', padding: 6, borderRadius: 4, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {enrolData.recovery_codes.join('\n')}
                  </pre>
                )}

                <h4 style={{ margin: '12px 0 4px', fontSize: 11, textTransform: 'uppercase' }}>3. Enter code</h4>
                <form onSubmit={submitEnrolCode} data-testid="mfa-enrol-form">
                  <input type="text" autoFocus maxLength={6} value={code}
                         onChange={(e) => setCode(e.target.value)}
                         placeholder="123456"
                         data-testid="mfa-enrol-code-input"
                         style={{ width: 120, fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, letterSpacing: 4, padding: 6, border: '1px solid var(--border)', borderRadius: 4 }} />
                  {error && <div className="auth-error" style={{ marginTop: 6 }} data-testid="mfa-enrol-error">{error}</div>}
                  <button type="submit" className="btn btn-primary btn-sm"
                          disabled={submitting || code.length < 6}
                          data-testid="mfa-enrol-submit" style={{ marginTop: 8, display: 'block' }}>
                    {submitting ? 'Verifying…' : 'Activate & sign in'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
      <footer className="auth-footer app-powered-footer" data-testid="login-footer"><span className="app-powered-footer-copy">© 2026 QUOD-Tech Ltd. All rights reserved.</span> <span className="app-powered-footer-sep" aria-hidden="true">·</span> <span className="app-powered-footer-mark">POWERED BY QUOD-TECH</span></footer>
    </div>
  );
}
